import { Head, Link } from '@inertiajs/react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Breadcrumbs } from '@/components/ui/breadcrumbs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  ArrowLeft, 
  Eye,
  MessageCircleQuestion,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

interface Chunk {
  id: number;
  chunk_collection: {
    id: number;
    name: string;
  } | null;
}

interface TestQuestion {
  id: number;
  question_text: string;
  answer_text?: string;
  status: string;
  metadata?: any;
  chunk_id: number;
  chunk: Chunk;
}

interface Document {
  id: number;
  name: string;
  description: string;
  status: string;
  'completed?'?: boolean;
}

interface TestQuestionsIndexProps {
  document: Document;
  test_questions: TestQuestion[];
  flash?: {
    notice?: string | null;
    alert?: string | null;
  };
}

export default function TestQuestionsIndex({ document, test_questions, flash }: TestQuestionsIndexProps) {
  const breadcrumbItems = [
    { label: 'Documents', href: '/documents' },
    { label: document.name, href: `/documents/${document.id}` },
    { label: 'Test Questions', href: '#' }
  ];

  const getStatusVariant = (status: string) => {
    const variants = {
      pending: 'bg-yellow-100 text-yellow-800',
      answered: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    } as const;
    
    return variants[status as keyof typeof variants] || 'bg-gray-100 text-gray-800';
  };

  const getCitedChunkIds = (metadata: any) => {
    if (!metadata) return [];
    
    // Check for different possible structures
    if (metadata.vector_data) {
      return metadata.vector_data.map((d: any) => d.chunk_id).filter((id: any) => id != null);
    }
    
    if (metadata.cited_chunks) {
      return metadata.cited_chunks;
    }
    
    if (metadata.chunk_ids) {
      return metadata.chunk_ids;
    }
    
    return [];
  };

  const getCitations = (metadata: any) => {
    if (!metadata) return [];
    
    // Check for different possible structures
    if (metadata.citations) {
      if (Array.isArray(metadata.citations)) {
        return metadata.citations.map((citation: any) => {
          if (typeof citation === 'string') return citation;
          if (typeof citation === 'object') return Object.keys(citation).join(', ');
          return String(citation);
        });
      }
      return [String(metadata.citations)];
    }
    
    if (metadata.sources) {
      return Array.isArray(metadata.sources) ? metadata.sources : [metadata.sources];
    }
    
    return [];
  };

  const trailingPreview = (text: string, max = 80) => {
    if (text.length <= max) return text;
    return `${text.substring(0, max)}...`;
  };

  const citesSourceChunk = (question: TestQuestion, citedChunkIds: number[]) => {
    return citedChunkIds.some((id) => Number(id) === Number(question.chunk_id));
  };

  return (
    <>
      <Head title={`Test Questions - ${document.name}`} />
      
      <div className="container mx-auto px-4 py-6 space-y-6">
        {flash?.alert ? (
          <Alert variant="destructive" role="alert">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{flash.alert}</AlertDescription>
          </Alert>
        ) : null}
        {flash?.notice ? (
          <Alert className="border-green-200 bg-green-50 text-green-900 dark:border-green-800 dark:bg-green-950 dark:text-green-100">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertTitle>Queued</AlertTitle>
            <AlertDescription>{flash.notice}</AlertDescription>
          </Alert>
        ) : null}

        <Breadcrumbs opsConsole items={breadcrumbItems} />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button asChild variant="outline" size="sm">
              <Link href={`/documents/${document.id}`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Document
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center">
                <MessageCircleQuestion className="h-6 w-6 mr-2" />
                Test Questions
              </h1>
              <p className="text-muted-foreground">
                {document.name} • {test_questions.length} questions
              </p>
            </div>
          </div>
        </div>

        {/* Test Questions Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Test Questions</CardTitle>
          </CardHeader>
          <CardContent>
            {test_questions.length === 0 ? (
              <div className="text-center py-8">
                <MessageCircleQuestion className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No test questions found for this document.</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Generate test questions from the document management page.
                </p>
              </div>
            ) : (
              <Table containerClassName="ordinal-scrollbar">
                <TableHeader>
                  <TableRow>
                    <TableHead>Chunk ID</TableHead>
                    <TableHead>Question</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Answer Preview</TableHead>
                    <TableHead>Citations</TableHead>
                    <TableHead>Citation Chunks</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {test_questions.map((question) => {
                    const citedChunkIds = getCitedChunkIds(question.metadata);
                    const citations = getCitations(question.metadata);
                    const citationPreview = citations.join(', ');
                    const missedSourceChunk =
                      question.status === 'answered' && !citesSourceChunk(question, citedChunkIds);
                    const isHighlighted = question.status === 'failed' || missedSourceChunk;
                    
                    return (
                      <TableRow 
                        key={question.id}
                        className={isHighlighted ? 'bg-red-50' : ''}
                      >
                        <TableCell className="font-medium">
                          {question.chunk_id}
                        </TableCell>
                        <TableCell className="max-w-md">
                          <div className="truncate" title={question.question_text}>
                            {question.question_text}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusVariant(question.status)}>
                            {question.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-sm">
                          {question.answer_text ? (
                            <div className="truncate" title={question.answer_text}>
                              {trailingPreview(question.answer_text)}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">No answer</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-sm">
                          {citations.length > 0 ? (
                            <div className="truncate text-sm" title={citationPreview}>
                              {trailingPreview(citationPreview)}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">None</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-sm">
                          {citedChunkIds.length > 0 ? (
                            <div className="truncate text-sm" title={citedChunkIds.join(', ')}>
                              {trailingPreview(citedChunkIds.join(', '))}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">None</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button asChild variant="outline" size="sm">
                            <Link href={`/documents/${document.id}/questions/${question.id}`}>
                              <Eye className="h-4 w-4 mr-2" />
                              View
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
